import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext.jsx";
import { api } from "../lib/api.js";
import { endHandoff, handoffUserId } from "../lib/handoff.js";
import { useWakeLock } from "../lib/useWakeLock.js";
import { biometricLogin, hasDevicePasskey } from "../lib/webauthn.js";
import { QrPanels } from "../pages/MyCard.jsx";

// Full-screen takeover while the phone is being passed around: only the QR
// panels are reachable, and the way back into the app is Face ID or the PIN.
// It renders above the router (see App.jsx), so the back button or any
// navigation underneath never dismisses it — and because the flag persists in
// localStorage, neither does a reload. The unlock endpoints mint a session
// from scratch, so it even survives the session cookie expiring mid-meetup.
export default function HandoffLock() {
  const { refresh } = useAuth();
  const [mode, setMode] = useState("exchange");
  const [card, setCard] = useState(null);
  const [exchangeUrl, setExchangeUrl] = useState("");
  const [unlocking, setUnlocking] = useState(false);
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const userId = handoffUserId();

  useWakeLock(true);

  useEffect(() => {
    api("/api/me/card")
      .then((d) => {
        setCard(d.card);
        setExchangeUrl(d.exchange_url || "");
      })
      .catch(() => {}); // session may have lapsed — site/music QRs still work
  }, []);

  // The overlay already covers whatever the back button reaches, but pinning a
  // history entry keeps the page from actually leaving this screen.
  useEffect(() => {
    window.history.pushState({ btHandoff: 1 }, "");
    const onPop = () => window.history.pushState({ btHandoff: 1 }, "");
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  async function unlockDone() {
    setBusy(false);
    endHandoff();
    await refresh(); // re-sync the session the unlock just minted/confirmed
  }

  async function tryFaceId() {
    setBusy(true);
    setError("");
    try {
      await biometricLogin(userId);
      await unlockDone();
    } catch (err) {
      setBusy(false);
      // Cancelled Face ID falls through to the PIN quietly.
      if (err?.name !== "NotAllowedError") {
        setError(err?.status === 429 ? "Too many attempts — try again later." : "Face ID didn't work — use your PIN.");
      }
    }
  }

  function openUnlock() {
    setUnlocking(true);
    setPin("");
    setError("");
    if (userId && hasDevicePasskey(userId)) tryFaceId();
  }

  async function submitPin(e) {
    e.preventDefault();
    if (pin.length < 4) return;
    setBusy(true);
    setError("");
    try {
      await api("/api/auth/pin", { method: "POST", body: { userId, pin } });
      await unlockDone();
    } catch (err) {
      setBusy(false);
      setPin("");
      setError(err?.status === 429 ? "Too many attempts — try again later." : "Incorrect PIN.");
    }
  }

  return (
    <div className="brand-gradient fixed inset-0 z-[100] overflow-y-auto">
      <div className="mx-auto flex min-h-full w-full max-w-md flex-col justify-center gap-4 px-4 py-8">
        {!unlocking ? (
          <>
            {card?.name && (
              <div className="text-center text-white">
                <p className="text-lg font-semibold">{card.name}</p>
                <p className="text-sm text-white/70">{[card.title, card.company].filter(Boolean).join(" · ")}</p>
              </div>
            )}

            <QrPanels mode={mode} onMode={setMode} card={card} exchangeUrl={exchangeUrl} showLink={false} />

            <p className="text-center text-xs text-white/60">Swipe or tap the tabs for more codes.</p>

            <button onClick={openUnlock} className="mx-auto mt-2 px-6 py-2 text-sm font-semibold text-white/80">
              🔓 Unlock
            </button>
          </>
        ) : (
          <div className="rounded-2xl bg-white/95 p-6 text-slate-900 shadow-xl">
            <p className="text-center text-sm font-medium text-slate-500">Unlock Business Tracker</p>

            {userId && hasDevicePasskey(userId) && (
              <button
                type="button"
                disabled={busy}
                onClick={tryFaceId}
                className="mt-4 w-full rounded-xl bg-navy-700 py-3 font-semibold text-white disabled:opacity-50"
              >
                {busy ? "Waiting for Face ID…" : "Unlock with Face ID"}
              </button>
            )}

            <form onSubmit={submitPin} className="mt-4 space-y-4">
              <label className="block text-sm font-medium text-slate-700">
                Enter your PIN
                <input
                  type="password"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
                  className="mt-1 w-full rounded-xl border border-slate-300 px-4 py-3 text-center text-2xl tracking-[0.5em] focus:border-navy-600 focus:outline-none"
                />
              </label>
              <button
                type="submit"
                disabled={busy || pin.length < 4}
                className="w-full rounded-xl bg-navy-700 py-3 font-semibold text-white disabled:opacity-50"
              >
                {busy ? "Working…" : "Unlock"}
              </button>
            </form>

            {error && <p className="mt-4 text-center text-sm text-red-600">{error}</p>}

            <button
              type="button"
              onClick={() => setUnlocking(false)}
              className="mt-4 w-full text-center text-sm text-slate-400"
            >
              ← Back to the QR codes
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
